// Feature: printable permit (owner: record work package). Route /permits/:id/print,
// shown without the app chrome. The page draws the permit with the same pure renderer
// the archive worker uses, inside a frame that cannot run script (sandbox without
// allow-scripts, plus the document's own Content-Security-Policy). The QR code only
// locates the live permit; it is never an authorisation.
import QRCode from 'qrcode'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ErrorBanner } from '@/components/evidence'
import { Banner, Button, Spinner } from '@/components/ui'
import { PERMIT_STATES, SYNTHETIC_NOTICE } from '@/domain/catalogue'
import type { PermitBundle } from '@/domain/types'
import { fmtDateTime } from '@/lib/format'
import { STAGING_NOTICE } from '@/domain/identity'
import { useMe, usePermit } from '@/lib/queries'
import { isStagingRecord, renderPermitHtml } from './renderPermitHtml'

type CopyNotice = { tone: 'warn' | 'bad' | 'info'; title: string; text: string }

const NOT_AUTHORISED = ['draft', 'returned_for_revision', 'submitted', 'reviewed']
const NO_WORK = ['cancellation_signed', 'closed_out', 'archived']

/** What the person holding this printout must know about it. */
function copyNotice(b: PermitBundle): CopyNotice {
  const state = b.permit.lifecycle_state
  const label = PERMIT_STATES[state]?.label ?? state
  if (NOT_AUTHORISED.includes(state)) {
    return { tone: 'warn', title: `Not authorised: ${label}`, text: 'This copy is for review only. It is not a permit to work.' }
  }
  if (NO_WORK.includes(state)) {
    return { tone: 'bad', title: `No work allowed: ${label}`, text: 'This permit has been cancelled. The copy is a record only.' }
  }
  if (state === 'authorised') {
    return { tone: 'info', title: 'Authorised permit', text: 'A printed copy is not an authorisation. Check the live permit before starting or restarting work.' }
  }
  return { tone: 'warn', title: `State: ${label}`, text: 'Check the live permit before relying on this copy.' }
}

const linkButton =
  'tap inline-flex items-center justify-center rounded-lg border border-line-strong bg-card px-4 font-semibold text-brand-dark hover:bg-brand-light'

export function PrintPage() {
  const { id } = useParams()
  const q = usePermit(id)
  // The database's own environment (0022): a staging copy is marked even before it has a number.
  const environmentKind = useMe().data?.environment_kind
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [qr, setQr] = useState<{ url: string; svg: string | null } | null>(null)
  const [loadedHtml, setLoadedHtml] = useState('')
  const [popupBlocked, setPopupBlocked] = useState(false)

  const locatorUrl = id ? `${window.location.origin}/permits/${encodeURIComponent(id)}` : ''

  useEffect(() => {
    if (!locatorUrl) return
    let cancelled = false
    QRCode.toString(locatorUrl, { type: 'svg', margin: 0 })
      .then((svg) => {
        if (!cancelled) setQr({ url: locatorUrl, svg })
      })
      .catch(() => {
        // Without a QR the copy still prints, with the address as text.
        if (!cancelled) setQr({ url: locatorUrl, svg: null })
      })
    return () => {
      cancelled = true
    }
  }, [locatorUrl])

  const bundle = q.data
  // Redraw only when the permit itself changes (row version), not on every 30-second
  // refetch, so the preview does not jump while someone is reading it.
  const versionKey = bundle ? `${bundle.permit.id}:${bundle.permit.row_version}` : ''
  const qrSvg = qr && qr.url === locatorUrl ? qr.svg : undefined
  const qrSettled = qrSvg !== undefined
  const html = useMemo(
    () =>
      bundle && qrSettled
        ? renderPermitHtml(bundle, { mode: 'print', qrSvg: qrSvg ?? undefined, locatorUrl, generatedAt: bundle.generated_at, environmentKind })
        : '',
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bundle is keyed by versionKey on purpose
    [versionKey, qrSettled, qrSvg, locatorUrl, environmentKind],
  )
  const ready = html !== '' && loadedHtml === html

  function printCopy() {
    const w = frameRef.current?.contentWindow
    if (!w) return
    w.focus()
    w.print()
  }

  /** Fallback for phones that print the whole page instead of the frame. */
  function openAsPage() {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
    const w = window.open(url, '_blank')
    if (w) {
      w.opener = null
      setPopupBlocked(false)
    } else {
      setPopupBlocked(true)
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  const backTo = id ? `/permits/${encodeURIComponent(id)}` : '/permits'

  if (q.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-paper">
        <Spinner />
      </div>
    )
  }
  if (!bundle) {
    return (
      <div className="mx-auto max-w-xl space-y-3 p-4">
        <ErrorBanner error={q.error ?? new Error('Permit not available')} title="Cannot load this permit" />
        <Link to={backTo} className={linkButton}>
          Back
        </Link>
      </div>
    )
  }

  const notice = copyNotice(bundle)

  return (
    <div className="flex h-dvh flex-col bg-paper">
      <div className="no-print shrink-0 border-b border-line bg-card">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2 px-3 py-2">
          <Link to={backTo} className={linkButton}>
            Back
          </Link>
          {/* On a phone the permit number gets its own line: it is the one thing this copy must show in full. */}
          <div className="order-last min-w-0 basis-full sm:order-none sm:flex-1 sm:basis-auto">
            <p className="break-words font-semibold text-ink">
              <span className="code">{bundle.permit.permit_number}</span> printable copy
            </p>
            <p className="text-xs text-ink-soft">Data read {fmtDateTime(bundle.generated_at)}</p>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" onClick={openAsPage} disabled={!ready}>
              Open as a page
            </Button>
            <Button onClick={printCopy} disabled={!ready} loading={html !== '' && !ready}>
              Print
            </Button>
          </div>
        </div>
        <div className="mx-auto max-w-5xl space-y-2 px-3 pb-2">
          {bundle.permit.is_synthetic ? (
            <Banner tone="synthetic" className="synthetic-stripe py-2">
              {SYNTHETIC_NOTICE}
            </Banner>
          ) : null}
          {isStagingRecord(bundle, environmentKind) ? (
            <Banner tone="warn" className="py-2">
              {STAGING_NOTICE}
            </Banner>
          ) : null}
          <Banner tone={notice.tone} title={notice.title} className="py-2">
            {notice.text}
          </Banner>
          {q.isError ? (
            <Banner tone="warn" title="Connection lost" className="py-2">
              This copy was loaded at {fmtDateTime(bundle.generated_at)} and may be out of date.
            </Banner>
          ) : null}
          {popupBlocked ? (
            <Banner tone="warn" className="py-2">
              The browser blocked the new tab. Allow pop-ups for this site, or use Print.
            </Banner>
          ) : null}
        </div>
      </div>
      <div className="min-h-0 flex-1 print:hidden">
        {html ? (
          <iframe
            key={versionKey}
            ref={frameRef}
            title={`Printable copy of permit ${bundle.permit.permit_number}`}
            srcDoc={html}
            sandbox="allow-same-origin allow-modals"
            onLoad={() => setLoadedHtml(html)}
            className="block h-full w-full border-0 bg-white"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        )}
      </div>
      <p className="hidden p-6 text-ink print:block">To print the permit, use the Print button on the print page.</p>
    </div>
  )
}
